import React from 'react';
import { Slide } from '../types';

interface SlidePatternOverlayProps {
  slide: Slide;
}

export const SlidePatternOverlay: React.FC<SlidePatternOverlayProps> = ({ slide }) => {
  if (!slide.backgroundPattern || slide.backgroundPattern === 'none') {
    return null;
  }

  const getPatternClassName = (): string => {
    switch (slide.backgroundPattern) {
      case 'glassmorphic':
        return 'backdrop-blur-md bg-white/10 border border-white/20';
      case 'mosaic':
        return '[filter:blur(2px)_contrast(1.5)] scale-110';
      case 'digital-mosaic':
        return 'backdrop-blur-xl backdrop-brightness-110 backdrop-contrast-125';
      case 'gaussian-blur':
        return 'backdrop-blur-lg bg-white/20 border border-white/30 rounded-xl shadow-xl';
      case 'fisheye':
        return 'rounded-full overflow-hidden [background:radial-gradient(circle_at_center,transparent_30%,rgba(0,0,0,0.3)_70%)]';
      case 'wide-angle':
        return 'overflow-hidden rounded-xl [transform:scaleX(1.3)_scaleY(0.9)] [clip-path:ellipse(90%_85%_at_50%_50%)]';
      default:
        return '';
    }
  };

  const getPatternStyle = (): React.CSSProperties => {
    switch (slide.backgroundPattern) {
      case 'grid-small':
        return {
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 8px, rgba(0,0,0,0.1) 8px, rgba(0,0,0,0.1) 9px), repeating-linear-gradient(90deg, transparent, transparent 8px, rgba(0,0,0,0.1) 8px, rgba(0,0,0,0.1) 9px)',
        };
      case 'grid-large':
        return {
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 20px, rgba(0,0,0,0.1) 20px, rgba(0,0,0,0.1) 21px), repeating-linear-gradient(90deg, transparent, transparent 20px, rgba(0,0,0,0.1) 20px, rgba(0,0,0,0.1) 21px)',
        };
      case 'grid-xlarge':
        return {
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(0,0,0,0.1) 40px, rgba(0,0,0,0.1) 41px), repeating-linear-gradient(90deg, transparent, transparent 40px, rgba(0,0,0,0.1) 40px, rgba(0,0,0,0.1) 41px)',
        };
      case 'lines-horizontal':
        return {
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 11px)',
        };
      case 'lines-vertical':
        return {
          backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 11px)',
        };
      case 'zigzag':
        return {
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.1) 10px, rgba(0,0,0,0.1) 20px)',
        };
      case 'wave-large':
        return {
          backgroundImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.1) 20%, transparent 50%)',
          backgroundSize: '100px 50px',
        };
      case 'wave-small':
        return {
          backgroundImage: 'radial-gradient(ellipse at center, rgba(0,0,0,0.08) 15%, transparent 35%)',
          backgroundSize: '40px 20px',
        };
      case 'circle-fractal':
        return {
          backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(0,0,0,0.1) 2px, transparent 2px), radial-gradient(circle at 75% 75%, rgba(0,0,0,0.1) 2px, transparent 2px)',
          backgroundSize: '30px 30px',
        };
      case 'water-drops':
        return {
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(0,0,0,0.1) 2px, transparent 5px), radial-gradient(circle at 80% 60%, rgba(0,0,0,0.08) 3px, transparent 6px), radial-gradient(circle at 60% 80%, rgba(0,0,0,0.06) 1px, transparent 4px)',
          backgroundSize: '60px 60px, 80px 80px, 40px 40px',
        };
      case 'rainbow':
        return {
          backgroundImage: 'linear-gradient(45deg, rgba(255,0,0,0.1) 0%, rgba(255,255,0,0.1) 17%, rgba(0,255,0,0.1) 33%, rgba(0,255,255,0.1) 50%, rgba(0,0,255,0.1) 67%, rgba(255,0,255,0.1) 83%, rgba(255,0,0,0.1) 100%)',
          backgroundSize: '200px 200px',
        };
      case 'glassmorphic':
      case 'mosaic':
      case 'digital-mosaic':
      case 'gaussian-blur':
      case 'fisheye':
      case 'wide-angle':
        // These are handled by Tailwind classes
        return {};
      default:
        return {};
    }
  };

  const className = getPatternClassName();
  const style = getPatternStyle();

  return (
    <div
      className={`slide-pattern-overlay-layer absolute inset-0 z-[5] pointer-events-none ${className}`}
      style={style}
    />
  );
};